<template>
    <div class="content-preview">
        <b-overlay :show="!content" rounded="sm">
            <article v-if="content" class="preview-article">
                <div class="article-header">
                    <div class="status-bar">
                        <div class="status-left">
                            <span :class="['status-indicator', content.IsActive ? 'active' : 'inactive']"></span>
                            <span class="status-text">{{ content.IsActive ? "Yayında" : "Taslak" }}</span>
                            <span v-if="content.IsFeatured" class="featured-badge">
                                <feather-icon icon="StarIcon" size="12" />
                            </span>
                        </div>
                        <span class="category-tag">{{ content.CategoryName }}</span>
                    </div>

                    <h1 class="article-title">{{ currentTranslation.Title }}</h1>

                    <div class="article-meta">
                        <span v-if="currentTranslation.Author" class="meta-item">
                            <feather-icon icon="UserIcon" size="14" />
                            {{ currentTranslation.Author }}
                        </span>
                        <span v-if="content.PublishDate" class="meta-item">
                            <feather-icon icon="CalendarIcon" size="14" />
                            {{ formatDate(content.PublishDate) }}
                        </span>
                        <span class="meta-item">
                            <feather-icon icon="EyeIcon" size="14" />
                            {{ content.ViewCount }}
                        </span>
                    </div>
                </div>

                <div class="article-main">
                    <div v-if="thumbnailImage" class="featured-image">
                        <b-img :src="getFileUrl(thumbnailImage.FilePath)" :alt="thumbnailImage.Alt || currentTranslation.Title" fluid />
                    </div>

                    <p v-if="currentTranslation.Summary" class="article-summary">
                        {{ currentTranslation.Summary }}
                    </p>

                    <div class="content-body" v-html="currentTranslation.Content"></div>
                </div>

                <div v-if="galleryImages.length > 0" class="gallery-section">
                    <div class="section-header">
                        <feather-icon icon="ImageIcon" size="16" />
                        <span>Galeri ({{ galleryImages.length }})</span>
                    </div>
                    <div class="gallery-grid">
                        <div v-for="media in galleryImages" :key="media.Id" class="gallery-item">
                            <b-img :src="getFileUrl(media.FilePath)" :alt="media.Alt" fluid thumbnail />
                        </div>
                    </div>
                </div>

                <div v-if="otherFiles.length > 0" class="files-section mb-2">
                    <div class="section-header">
                        <feather-icon icon="PaperclipIcon" size="16" />
                        <span>Diğer Dosyalar</span>
                    </div>
                    <div class="files-list">
                        <div v-for="file in otherFiles" :key="file.Id" class="file-item d-flex align-items-center p-1 border rounded mb-1 bg-light">
                            <feather-icon :icon="getFileIcon(file.MediaType)" size="20" class="mr-1 text-primary" />
                            <div class="d-flex flex-column">
                                <span class="font-weight-bold text-dark">{{ file.Title || file.FileName }}</span>
                                <small class="text-muted">{{ file.MediaType }}</small>
                            </div>
                            <div class="ml-auto">
                                <a :href="getFileUrl(file.FilePath)" target="_blank" class="btn btn-sm btn-flat-primary">
                                    <feather-icon icon="DownloadIcon" size="14" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="currentTranslation.SeoTitle" class="seo-section">
                    <div class="section-header">
                        <feather-icon icon="SearchIcon" size="16" />
                        <span>SEO Bilgileri</span>
                    </div>
                    <div class="seo-content">
                        <div class="seo-item">
                            <span class="seo-label">Başlık</span>
                            <p class="seo-value">{{ currentTranslation.SeoTitle }}</p>
                        </div>
                        <div class="seo-item">
                            <span class="seo-label">Açıklama</span>
                            <p class="seo-value">{{ currentTranslation.SeoDescription }}</p>
                        </div>
                        <div v-if="currentTranslation.SeoKeywords" class="seo-item">
                            <span class="seo-label">Anahtar Kelimeler</span>
                            <div class="keywords">
                                <span v-for="(keyword, index) in getKeywords" :key="index" class="keyword-tag">
                                    {{ keyword }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="content.MetaData && content.MetaData.length" class="metadata-section">
                    <div class="section-header">
                        <feather-icon icon="CodeIcon" size="16" />
                        <span>Meta Data</span>
                    </div>
                    <div class="metadata-content">
                        <div v-for="meta in content.MetaData" :key="meta.Id" class="metadata-item">
                            <span class="metadata-label">{{ meta.MetaGroup }}.{{ meta.MetaKey }}</span>
                            <code class="metadata-value">{{ meta.MetaValue }}</code>
                        </div>
                    </div>
                </div>
            </article>
        </b-overlay>
    </div>
</template>

<script>
import { BOverlay, BImg, BBadge } from "bootstrap-vue";
import Utilities from "@/utilities/system/index";

export default {
    name: "ContentPreview",
    components: {
        BOverlay,
        BImg,
        BBadge,
    },
    props: {
        content: {
            type: Object,
            default: null,
        },
        language: {
            type: String,
            default: "tr",
        },
    },
    computed: {
        currentTranslation() {
            if (!this.content?.Translations) return {};
            return (
                this.content.Translations.find((t) => t.LanguageCode === this.language) ||
                this.content.Translations.find((t) => t.LanguageCode === "tr") || // Fallback TR
                this.content.Translations[0] ||
                {}
            );
        },
        allImages() {
            if (!this.content?.MediaFiles) return [];
            return this.content.MediaFiles.filter((m) => m.MediaType === "image").sort((a, b) => a.SortOrder - b.SortOrder);
        },
        thumbnailImage() {
            return this.allImages.length > 0 ? this.allImages[0] : null;
        },
        galleryImages() {
            return this.allImages.length > 1 ? this.allImages.slice(1) : [];
        },
        otherFiles() {
            if (!this.content?.MediaFiles) return [];
            return this.content.MediaFiles.filter((m) => m.MediaType !== "image").sort((a, b) => a.SortOrder - b.SortOrder);
        },
        getKeywords() {
            if (!this.currentTranslation.SeoKeywords) return [];
            return this.currentTranslation.SeoKeywords.split(",").map((k) => k.trim());
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return "";
            const options = { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" };
            return new Date(date).toLocaleDateString("tr-TR", options);
        },
        getFileUrl(path) {
            if (!path) return "";
            if (path.startsWith("http")) return path;

            let baseUrl = Utilities.BaseURL.URL;
            if (!baseUrl || baseUrl === "undefined" || baseUrl === "") {
                baseUrl = "https://localhost:7287";
            }
            return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
        },
        getFileIcon(mediaType) {
            const icons = {
                video: "VideoIcon",
                audio: "MusicIcon",
                pdf: "FileTextIcon",
                default: "FileIcon",
            };
            return icons[mediaType] || icons.default;
        },
    },
};
</script>

<style lang="scss" scoped>
.content-preview {
    .preview-article {
        max-width: 800px;
        margin: 0 auto;

        .article-header {
            padding: 1.5rem;
            background: #fff;
            border-radius: 8px;
            box-shadow: 3px 10px 15px rgba(0, 0, 0, 0.06);
            margin-bottom: 1.5rem;

            .status-bar {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 1.25rem;

                .status-left {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;

                    .status-indicator {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;

                        &.active {
                            background: #28c76f;
                        }

                        &.inactive {
                            background: #ea5455;
                        }
                    }

                    .status-text {
                        font-size: 0.8125rem;
                        font-weight: 500;
                        color: #6e6b7b;
                    }

                    .featured-badge {
                        display: flex;
                        color: #ff9f43;
                    }
                }

                .category-tag {
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #7367f0;
                }
            }

            .article-title {
                font-size: 2.25rem;
                font-weight: 700;
                line-height: 1.25;
                margin-bottom: 1rem;
                color: #5e5873;
            }

            .article-meta {
                display: flex;
                gap: 1.25rem;
                padding-top: 1rem;
                border-top: 1px solid #ebe9f1;

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    font-size: 0.875rem;
                    color: #b9b9c3;
                }
            }
        }

        .article-main {
            padding: 1.5rem;
            background: #fff;
            border-radius: 8px;
            box-shadow: 3px 10px 15px rgba(0, 0, 0, 0.06);
            margin-bottom: 1.5rem;

            .featured-image {
                margin: -1.5rem -1.5rem 1.5rem -1.5rem;
                border-radius: 8px 8px 0 0;
                overflow: hidden;

                img {
                    width: 100%;
                    display: block;
                }
            }

            .article-summary {
                font-size: 0.9375rem;
                line-height: 1.7;
                color: #b9b9c3;
                font-style: italic;
                margin-bottom: 1.5rem;
                padding-left: 1rem;
                border-left: 3px solid #ebe9f1;
            }

            .content-body {
                font-size: 1rem;
                line-height: 1.75;
                color: #5e5873;

                ::v-deep {
                    p {
                        margin-bottom: 1.25rem;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }

                    h1,
                    h2,
                    h3,
                    h4,
                    h5,
                    h6 {
                        font-weight: 600;
                        margin-top: 2rem;
                        margin-bottom: 1rem;
                        color: #5e5873;

                        &:first-child {
                            margin-top: 0;
                        }
                    }

                    h1 {
                        font-size: 1.75rem;
                    }
                    h2 {
                        font-size: 1.5rem;
                    }
                    h3 {
                        font-size: 1.25rem;
                    }

                    img {
                        max-width: 100%;
                        border-radius: 6px;
                        margin: 1.5rem 0;
                    }

                    ul,
                    ol {
                        margin-bottom: 1.25rem;
                        padding-left: 1.5rem;
                    }

                    blockquote {
                        margin: 1.5rem 0;
                        padding-left: 1rem;
                        border-left: 3px solid #7367f0;
                        color: #6e6b7b;
                        font-style: italic;
                    }

                    code {
                        background: #f8f8f8;
                        padding: 0.125rem 0.375rem;
                        border-radius: 3px;
                        font-size: 0.875em;
                    }

                    a {
                        color: #7367f0;
                        text-decoration: none;

                        &:hover {
                            text-decoration: underline;
                        }
                    }
                }
            }
        }

        .gallery-section,
        .seo-section,
        .metadata-section {
            padding: 1.5rem;
            background: #fff;
            border-radius: 8px;
            box-shadow: 3px 10px 15px rgba(0, 0, 0, 0.06);
            margin-bottom: 1.5rem;

            .section-header {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                font-size: 0.9375rem;
                font-weight: 600;
                color: #5e5873;
                margin-bottom: 1.25rem;
                padding-bottom: 0.75rem;
                border-bottom: 2px solid #ebe9f1;
            }
        }

        .gallery-section {
            .gallery-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                gap: 1rem;

                .gallery-item {
                    border-radius: 6px;
                    overflow: hidden;
                    border: 1px solid #ebe9f1;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        display: block;
                        transition: transform 0.2s;
                    }

                    &:hover img {
                        transform: scale(1.05);
                    }
                }
            }
        }

        .seo-section {
            .seo-content {
                display: flex;
                flex-direction: column;
                gap: 1rem;

                .seo-item {
                    .seo-label {
                        display: block;
                        font-size: 0.75rem;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #b9b9c3;
                        margin-bottom: 0.5rem;
                    }

                    .seo-value {
                        font-size: 0.9375rem;
                        line-height: 1.6;
                        color: #5e5873;
                        margin: 0;
                    }

                    .keywords {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 0.5rem;

                        .keyword-tag {
                            padding: 0.25rem 0.75rem;
                            background: #f8f8f8;
                            border: 1px solid #ebe9f1;
                            border-radius: 3px;
                            font-size: 0.8125rem;
                            color: #6e6b7b;
                        }
                    }
                }
            }
        }

        .metadata-section {
            .metadata-content {
                display: flex;
                flex-direction: column;
                gap: 0.75rem;

                .metadata-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: #fafafa;
                    border-radius: 4px;
                    border: 1px solid #ebe9f1;

                    .metadata-label {
                        font-size: 0.8125rem;
                        color: #6e6b7b;
                        font-weight: 500;
                    }

                    .metadata-value {
                        font-size: 0.8125rem;
                        padding: 0.375rem 0.625rem;
                        background: #fff;
                        border: 1px solid #ebe9f1;
                        border-radius: 3px;
                        color: #5e5873;
                    }
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .content-preview {
        .preview-article {
            .article-header {
                .article-title {
                    font-size: 1.75rem;
                }

                .article-meta {
                    flex-wrap: wrap;
                    gap: 0.75rem;
                }
            }

            .gallery-section {
                .gallery-grid {
                    grid-template-columns: repeat(2, 1fr);
                }
            }

            .metadata-section {
                .metadata-content {
                    .metadata-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                }
            }
        }
    }
}
</style>
